'use client';

import React, { useMemo, useCallback } from 'react';
import { StrategyCodeSetDropdown, StrategyFilterSetDropdown } from '../';
import { useStrategyForm } from 'src/hooks/strategy/useStrategyForm';
import { StrategyConfigProps } from 'src/components/Strategy/config';

import { TreatmentToggle, ProspectTypeToggle } from 'src/components/Toggles';

const FreshFacesConfiguration: React.FC<StrategyConfigProps> = () => {
  const { freshFacesData, updateFreshFacesData } = useStrategyForm();

  // Handler for volume type toggle (treating/referring)
  const handleVolumeTypeChange = useCallback(
    (value: 'treating' | 'sending') => {
      updateFreshFacesData({
        volume_type: value === 'sending' ? 'referring' : 'treating',
      });
    },
    [updateFreshFacesData]
  );

  // Handler for type toggle (hcp/site)
  const handleTypeChange = useCallback(
    (value: 'hcp' | 'site') => {
      // If switching to Sites and prospect mode is 'referring', change to 'treating'
      if (value === 'site' && freshFacesData?.volume_type === 'referring') {
        updateFreshFacesData({
          provider_type: value,
          volume_type: 'treating',
        });
      } else {
        updateFreshFacesData({ provider_type: value });
      }
    },
    [updateFreshFacesData, freshFacesData?.volume_type]
  );

  // Function to check if a value is disabled
  const handleIsDisabled = useCallback(
    (value: string) => {
      // Disable 'sending' (referring) mode if type is 'site'
      return value === 'sending' && freshFacesData?.provider_type === 'site';
    },
    [freshFacesData?.provider_type]
  );

  const selectedCodeSetId = useMemo(() => {
    return typeof freshFacesData?.code_set === 'object' &&
      freshFacesData.code_set !== null
      ? String(freshFacesData.code_set.id)
      : freshFacesData?.code_set
      ? String(freshFacesData.code_set)
      : undefined;
  }, [freshFacesData?.code_set]);

  return (
    <div
      className="flex flex-col pt-3 gap-4 border-t border-neutral-200 mt-3"
      data-testid="fresh-faces-configuration"
    >
      {/* Row 1: Type + Source Toggles */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <div
          className="flex flex-col gap-2"
          data-testid="fresh-faces-type-container"
        >
          <label className="text-primary text-sm font-semibold font-['Inter'] leading-tight">
            Type
          </label>
          <ProspectTypeToggle
            value={freshFacesData?.provider_type}
            onChange={handleTypeChange}
          />
        </div>

        <div
          className="flex flex-col gap-2"
          data-testid="fresh-faces-source-container"
        >
          <label className="text-primary text-sm font-semibold font-['Inter'] leading-tight">
            Source
          </label>
          <TreatmentToggle
            value={
              freshFacesData?.volume_type === 'referring'
                ? 'sending'
                : 'treating'
            }
            onChange={handleVolumeTypeChange}
            isDisabled={handleIsDisabled}
          />
        </div>
      </div>

      {/* Row 2: Code Set + Filter Set Dropdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <div data-testid="fresh-faces-code-set-container">
          <StrategyCodeSetDropdown
            selectedCodeSetId={selectedCodeSetId}
            onCodeSetSelection={(codeSet) =>
              updateFreshFacesData({
                code_set: codeSet
                  ? { id: codeSet.id, label: codeSet.label, type: codeSet.type }
                  : undefined,
              })
            }
          />
        </div>

        <div data-testid="fresh-faces-filter-set-container">
          <StrategyFilterSetDropdown
            selectedFilterSetId={
              typeof freshFacesData?.filter_set === 'object' &&
              freshFacesData.filter_set !== null
                ? String(freshFacesData.filter_set.id)
                : freshFacesData?.filter_set
                ? String(freshFacesData.filter_set)
                : undefined
            }
            onFilterSetSelection={(filterSet) =>
              updateFreshFacesData({
                filter_set: filterSet
                  ? { id: filterSet.id, label: filterSet.label, type: filterSet.type }
                  : undefined,
              })
            }
          />
        </div>
      </div>
    </div>
  );
};

export default FreshFacesConfiguration;
